/**
 * Cookie consent state.
 *
 * The server is the source of truth: it renders `window.cookieConsent` into the
 * page and gates anything server-rendered on the same value. This module keeps a
 * reactive copy for the UI, and notifies subscribers when a decision changes so
 * the tag loader can act on it without a page reload.
 */
import { reactive, readonly } from 'vue'
import { createJSONRequest } from '../api/create-request'

export const CONSENT_CATEGORIES = {
  NECESSARY: 'necessary',
  FUNCTIONAL: 'functional',
  ANALYTICS: 'analytics',
  MARKETING: 'marketing',
}

export const CONSENT_ACTIONS = {
  ACCEPT_ALL: 'accept_all',
  REJECT_ALL: 'reject_all',
  CUSTOM: 'custom',
}

/** Categories the visitor can switch on or off, in the order they are shown. */
export const OPTIONAL_CATEGORIES = [
  CONSENT_CATEGORIES.FUNCTIONAL,
  CONSENT_CATEGORIES.ANALYTICS,
  CONSENT_CATEGORIES.MARKETING,
]

const deniedState = () => ({
  [CONSENT_CATEGORIES.NECESSARY]: true,
  [CONSENT_CATEGORIES.FUNCTIONAL]: false,
  [CONSENT_CATEGORIES.ANALYTICS]: false,
  [CONSENT_CATEGORIES.MARKETING]: false,
})

const bootstrap = (typeof window !== 'undefined' && window.cookieConsent) || {}

const state = reactive({
  /** null until the visitor has made a decision — that is when we must ask. */
  decision: bootstrap.decision || null,
  granted: { ...deniedState(), ...(bootstrap.granted || {}) },
  policyId: bootstrap.policyId ?? null,
  policyVersion: bootstrap.policyVersion ?? null,
  saving: false,
  /**
   * True while another blocking dialog owns the screen. The banner cannot be
   * dismissed, so it must never stack on top of one: it waits its turn.
   */
  deferred: false,
})

const subscribers = new Set()

function notify () {
  subscribers.forEach(callback => {
    try {
      callback(readonly(state.granted))
    } catch (e) {
      console.error('[COOKIE CONSENT] subscriber failed', e)
    }
  })
}

/**
 * Run `callback` with the granted categories now, and again whenever the
 * visitor changes their decision. Returns an unsubscribe function.
 */
export function onConsentChange (callback) {
  subscribers.add(callback)
  callback(readonly(state.granted))

  return () => subscribers.delete(callback)
}

export function consentState () {
  return readonly(state)
}

export function hasDecided () {
  return state.decision !== null
}

/**
 * Hold the banner back while another dialog owns the screen. A legal policy
 * announcement that covers more than the cookie policy has to be acknowledged
 * whatever the visitor decides here, so it goes first and the banner waits
 * behind it instead of stacking on top of it.
 */
export function deferConsentDialog () {
  state.deferred = true
}

/** Give the screen back: the banner returns if a decision is still outstanding. */
export function releaseConsentDialog () {
  state.deferred = false
}

export function isGranted (category) {
  return state.granted[category] === true
}

function applyPayload (payload) {
  if (!payload) {
    return
  }

  state.decision = payload.decision || null
  state.granted = { ...deniedState(), ...(payload.granted || {}) }
  state.policyId = payload.policyId ?? state.policyId
  state.policyVersion = payload.policyVersion ?? state.policyVersion
}

/**
 * Persist a decision. The server sets the consent cookie on the response, so a
 * later full page load gates server-rendered output on the same choice.
 */
async function save (action, categories = {}) {
  state.saving = true

  try {
    const payload = await createJSONRequest({
      url: '/api/cookie-consent',
      method: 'POST',
      data: { action, categories },
    })

    // `createJSONRequest` resolves with the error body on a failed response
    // (429, 419, 500) rather than rejecting, and that body has no `decision`.
    // Applying it would silently wipe the choice the visitor just made and
    // leave the consent dialog up with no explanation, so refuse it instead.
    if (!isConsentPayload(payload)) {
      throw new Error('[COOKIE CONSENT] refusing to apply a non-consent response')
    }

    applyPayload(payload)
    notify()

    return payload
  } finally {
    state.saving = false
  }
}

/** A saved decision always comes back with both a decision and its categories. */
function isConsentPayload (payload) {
  return !!payload &&
    typeof payload === 'object' &&
    !!payload.decision &&
    typeof payload.decision === 'object' &&
    !!payload.granted &&
    typeof payload.granted === 'object'
}

/**
 * Re-read the decision from the server and adopt it.
 *
 * Needed when something other than this module established one: acknowledging a
 * policy announcement that carried the cookie policy sets the safe default
 * server-side, and without this the banner would still come up in the same page
 * view asking what has just been settled.
 *
 * A response carrying no decision is ignored rather than applied, so this can
 * never wipe a choice that is already on file.
 */
export async function refreshConsent () {
  const payload = await createJSONRequest({
    url: '/api/cookie-consent',
    method: 'GET',
  })

  if (!isConsentPayload(payload)) {
    return null
  }

  applyPayload(payload)
  notify()

  return payload
}

export function acceptAll () {
  return save(CONSENT_ACTIONS.ACCEPT_ALL)
}

/** Refusing is one click, exactly like accepting. */
export function rejectAll () {
  return save(CONSENT_ACTIONS.REJECT_ALL)
}

/**
 * Save a per-category choice. A previously granted category that is switched
 * off here is recorded server-side as a withdrawal.
 */
export function saveSelection (categories) {
  const selection = {}

  OPTIONAL_CATEGORIES.forEach(category => {
    selection[category] = categories[category] === true
  })

  const noneGranted = OPTIONAL_CATEGORIES.every(category => !selection[category])
  const allGranted = OPTIONAL_CATEGORIES.every(category => selection[category])

  if (noneGranted) {
    return save(CONSENT_ACTIONS.REJECT_ALL)
  }

  if (allGranted) {
    return save(CONSENT_ACTIONS.ACCEPT_ALL)
  }

  return save(CONSENT_ACTIONS.CUSTOM, selection)
}

/** Withdraw everything the visitor previously agreed to. */
export function withdrawAll () {
  return save(CONSENT_ACTIONS.REJECT_ALL)
}
